import { Link } from "react-router-dom"
import {ArrowRight, Shield, Headphones, Server, Clock, CheckCircle2, Monitor, Wrench, Wifi, HardDrive, Users, TicketCheck, ChevronRight, Zap, Star} from 'lucide-react'
import SectionHeading from "@/components/SectionHeading"
import InlineEdit from "@/components/InlineEdit"
import { useEditMode } from "@/contexts/EditModeContext"

const heroImage = "https://static.lumi.new/07/0730dab9a3735ad61a4c5b24fcc71ce4.jpg"

const quickAccess = [
  {
    icon: TicketCheck,
    label: "Ouvrir un ticket",
    desc: "Déclarez un problème technique",
    path: "/support",
    color: "from-cyan-500 to-blue-500",
  },
  {
    icon: Clock,
    label: "Prendre rendez-vous",
    desc: "Planifiez un appel ou un audit",
    path: "/rendez-vous",
    color: "from-emerald-500 to-teal-500",
  },
  {
    icon: Headphones,
    label: "Contacter LJJ IT",
    desc: "Envoyez-nous un message",
    path: "/contact",
    color: "from-amber-500 to-orange-500",
  },
]

const defaultServices = [
  { icon: Headphones, key: "home_svc_1", title: "Support informatique", desc: "Assistance réactive pour vos équipes" },
  { icon: Wrench, key: "home_svc_2", title: "Maintenance", desc: "Prévention et entretien de votre parc" },
  { icon: Monitor, key: "home_svc_3", title: "Dépannage à distance", desc: "Intervention rapide sans déplacement" },
  { icon: Server, key: "home_svc_4", title: "Infrastructure", desc: "Réseau, serveurs et cloud" },
  { icon: HardDrive, key: "home_svc_5", title: "Sauvegarde & Sécurité", desc: "Protection de vos données" },
  { icon: Users, key: "home_svc_6", title: "Assistance utilisateurs", desc: "Formation et accompagnement" },
]

const defaultStats = [
  { key: "home_stat_1", value: "500+", label: "Interventions / an" },
  { key: "home_stat_2", value: "98%", label: "Satisfaction client" },
  { key: "home_stat_3", value: "<2h", label: "Temps de réponse" },
  { key: "home_stat_4", value: "24/7", label: "Surveillance active" },
]

const defaultReassurance = [
  { key: "home_reassure_1", text: "Interlocuteur unique et dédié" },
  { key: "home_reassure_2", text: "Contrats flexibles et sans engagement" },
  { key: "home_reassure_3", text: "Équipe certifiée et expérimentée" },
  { key: "home_reassure_4", text: "Outils de suivi transparents" },
  { key: "home_reassure_5", text: "Interventions sur site et à distance" },
  { key: "home_reassure_6", text: "Tarifs clairs et sans surprise" },
]

export default function HomePage() {
  const { isEditMode } = useEditMode()

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="LJJ IT Infrastructure"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
        </div>

        <div className="relative section-padding w-full pt-28 pb-20">
          <div className="max-w-3xl">
            <div className="animate-fade-in-up mb-8">
              <img 
                src="https://static.lumi.new/3d/3d3904167daeab807172c4efcb0502aa.webp" 
                alt="LJJ IT Logo" 
                className="w-72 sm:w-96 lg:w-[28rem] h-auto object-contain mb-6 bg-white rounded-xl px-4 py-3 drop-shadow-2xl"
              />
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium">
                <Zap className="w-3.5 h-3.5" />
                <InlineEdit contentKey="home_badge" defaultValue="Support informatique professionnel" className="text-cyan-400 text-xs font-medium" />
              </span>
            </div>

            {isEditMode ? (
              <InlineEdit
                contentKey="home_headline"
                defaultValue="Votre informatique, centralisée et maîtrisée"
                as="h1"
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] text-white"
              />
            ) : (
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
                Votre informatique,{" "}
                <span className="gradient-text">centralisée</span>{" "}
                et{" "}
                <span className="gradient-text">maîtrisée</span>
              </h1>
            )}

            <div className="mt-6 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
              <InlineEdit
                contentKey="home_subtitle"
                defaultValue="Support technique, maintenance, infrastructure et gestion des demandes — le tout au même endroit. LJJ IT accompagne les PME et indépendants avec un service réactif et structuré."
                as="p"
                className="text-lg sm:text-xl text-slate-400 leading-relaxed max-w-2xl"
                multiline
              />
            </div>

            <div className="mt-8 flex flex-wrap gap-4 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
              <Link to="/support" className="btn-primary text-base !py-3.5 !px-8">
                Ouvrir un ticket
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/services" className="btn-secondary text-base !py-3.5 !px-8">
                Découvrir nos services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick access */}
      <section className="section-padding -mt-16 relative z-10 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {quickAccess.map((item, i) => (
            <Link
              key={item.label}
              to={item.path}
              className="group card-hover-lift !p-5 flex items-start gap-4 animate-fade-in-up"
              style={{ animationDelay: `${0.1 * i}s` }}
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                <item.icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-white group-hover:text-cyan-400 transition-colors">
                  {item.label}
                </h3>
                <p className="text-sm text-slate-400 mt-0.5">{item.desc}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all shrink-0 mt-1" />
            </Link>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {defaultStats.map((stat, i) => (
            <div
              key={stat.key}
              className="text-center animate-fade-in-up"
              style={{ animationDelay: `${0.1 * i}s` }}
            >
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold gradient-text mb-2">
                <InlineEdit contentKey={`${stat.key}_value`} defaultValue={stat.value} className="text-3xl sm:text-4xl lg:text-5xl font-bold" />
              </div>
              <div className="text-sm text-slate-400">
                <InlineEdit contentKey={`${stat.key}_label`} defaultValue={stat.label} className="text-sm text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Services overview */}
      <section className="section-padding py-20 lg:py-28">
        <SectionHeading
          badge="Nos services"
          title="Des solutions IT"
          highlight="sur mesure"
          description="De la maintenance préventive au dépannage urgent, nous couvrons tous vos besoins informatiques avec réactivité et expertise."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {defaultServices.map((service, i) => (
            <div
              key={service.key}
              className="card-hover-lift group animate-fade-in-up"
              style={{ animationDelay: `${0.05 * i}s` }}
            >
              <div className="w-11 h-11 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-colors">
                <service.icon className="w-5.5 h-5.5 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                <InlineEdit contentKey={`${service.key}_title`} defaultValue={service.title} className="text-lg font-semibold text-white" />
              </h3>
              <InlineEdit contentKey={`${service.key}_desc`} defaultValue={service.desc} as="p" className="text-sm text-slate-400 leading-relaxed" />
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/services" className="btn-secondary">
            Voir tous nos services
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Support highlight */}
      <section className="section-padding py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <SectionHeading
              badge="Support structuré"
              title="Un système de tickets"
              highlight="professionnel"
              description="Fini les demandes perdues par email ou WhatsApp. Centralisez, suivez et résolvez chaque problème avec notre portail de support intégré."
              centered={false}
            />
            <div className="space-y-3">
              {[
                { key: "home_support_1", text: "Création de ticket en quelques clics" },
                { key: "home_support_2", text: "Suivi en temps réel du statut" },
                { key: "home_support_3", text: "Historique complet des échanges" },
                { key: "home_support_4", text: "Pièces jointes et captures d'écran" },
                { key: "home_support_5", text: "Notifications automatiques par email" },
                { key: "home_support_6", text: "Priorité et catégorisation intelligente" },
              ].map((item) => (
                <div key={item.key} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <InlineEdit contentKey={item.key} defaultValue={item.text} as="span" className="text-slate-300 text-sm" />
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/support" className="btn-primary">
                Accéder au support
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-cyan-500/10 to-emerald-500/10 rounded-3xl blur-2xl" />
            <img
              src="https://static.lumi.new/1c/1cb5350f7ea110d02424c10e86557beb.png"
              alt="Système de support LJJ IT"
              className="relative rounded-2xl border border-slate-800 shadow-2xl shadow-cyan-500/5 w-full"
            />
          </div>
        </div>
      </section>

      {/* Reassurance */}
      <section className="section-padding py-20 lg:py-28 border-t border-slate-800/50">
        <SectionHeading
          badge="Pourquoi LJJ IT"
          title="La confiance,"
          highlight="en toute transparence"
          description="Nous construisons des relations durables avec nos clients grâce à un service clair, honnête et professionnel."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {defaultReassurance.map((item, i) => (
            <div
              key={item.key}
              className="flex items-center gap-3 p-4 rounded-xl bg-slate-900/40 border border-slate-800/50 animate-fade-in-up"
              style={{ animationDelay: `${0.05 * i}s` }}
            >
              <Star className="w-5 h-5 text-amber-400 shrink-0" />
              <InlineEdit contentKey={item.key} defaultValue={item.text} as="span" className="text-sm text-slate-300" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="section-padding py-20 lg:py-28">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-cyan-500/10 via-slate-900 to-emerald-500/10 border border-slate-800 p-10 sm:p-14 lg:p-20 text-center">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
          <div className="relative">
            <InlineEdit
              contentKey="home_cta_title"
              defaultValue="Prêt à structurer votre IT ?"
              as="h2"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            />
            <InlineEdit
              contentKey="home_cta_desc"
              defaultValue="Contactez-nous pour un premier échange gratuit et sans engagement. Découvrez comment LJJ IT peut simplifier votre quotidien."
              as="p"
              className="text-lg text-slate-400 max-w-xl mx-auto mb-8"
              multiline
            />
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/rendez-vous" className="btn-primary text-base !py-3.5 !px-8">
                Prendre rendez-vous
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/contact" className="btn-secondary text-base !py-3.5 !px-8">
                Nous contacter
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
